/**
 * Plan Limits and Usage Calculations
 * Handles subscription tiers with canonical mapping
 */

import type { SubscriptionTier } from '../types/subscription'
import { DISPLAY_NAME, normalizeTier, isAIEnabled } from '../billing/tiers'

export type Tier = 'free_trial' | SubscriptionTier;

export interface Usage {
  used: number;
  limit: number;
  remaining: number;
  tier: Tier;
}

/**
 * Plan limits configuration
 * Note: core_plus and founding_member have effectively unlimited plans
 */
const PLAN_LIMITS: Record<Tier, number> = {
  free_trial: 1,          // 1 plan during trial
  demo: 1,                // Demo limited to 1
  starter: 3,             // Equivalent to former Duo limit
  founding_member: 9999,  // Same limits as core_plus (core features + AI)
  core_plus: 9999,        // Effectively unlimited
};

/**
 * Compute usage statistics for a given tier and usage
 * Ensures remaining never goes below 0 (fixes "-1 remaining" bug)
 */
export function computeUsage(
  tier: Tier, 
  used: number, 
  billingResetsAt?: Date
): Usage {
  const baseTier = tier === 'free_trial' ? 'free_trial' : normalizeTier(tier);
  const limit = PLAN_LIMITS[baseTier as Tier] ?? 1;
  
  // Ensure used count is never negative
  const safeUsed = Math.max(0, used);
  
  // Calculate remaining, never below 0
  const remaining = Math.max(0, limit - safeUsed);

  return {
    used: safeUsed,
    limit,
    remaining,
    tier: baseTier as Tier
  };
}

/**
 * Check if user can create another plan
 */
export function canCreatePlan(usage: Usage): boolean {
  return usage.remaining > 0;
}

/**
 * Get user-friendly tier display name
 */
export function getTierDisplayName(tier: Tier): string {
  if (tier === 'free_trial') return 'Free Trial';
  const normalized = normalizeTier(tier);
  return DISPLAY_NAME[normalized] ?? 'Demo';
}

/**
 * Reset usage for billing cycle (called when billing resets)
 */
export function resetUsageForBillingCycle(tier: Tier): Usage {
  return computeUsage(tier, 0);
}

/**
 * Check if tier has unlimited plans (for UI logic)
 */
export function isUnlimitedTier(tier: Tier): boolean {
  const normalized = tier === 'free_trial' ? 'demo' : normalizeTier(tier);
  return isAIEnabled(normalized) && normalized === 'core_plus';
}
